'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics and Thyroid: How Plastic Chemicals Disrupt Hormones"
      author="MicroPlastics Editorial Team"
      date="May 15, 2026"
      readTime="9 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics and thyroid function, endocrine disruption from plastic chemicals"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Microplastics carry <strong>endocrine-disrupting chemicals</strong> (BPA, BPS, BPF, phthalates, and PFAS) that are well-documented to
        interfere with thyroid hormone synthesis, transport, and receptor
        binding. Multiple human studies have linked plastic-chemical exposure
        to <strong>altered TSH, T3, and T4 levels</strong>, reduced fertility,
        and elevated risk of thyroid nodules. The strongest population-level
        evidence comes from biomonitoring studies (CDC NHANES) consistently
        showing dose-response associations between urinary plastic chemicals
        and thyroid biomarkers. Reducing dietary plastic contact is the most
        evidence-supported intervention.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The thyroid is unusually sensitive to environmental chemicals because thyroid hormones direct nearly every metabolic process.</>,
          <>BPA, BPS, BPF, phthalates, and PFAS (all carried by or leached from microplastics) interfere with thyroid hormone receptor binding.</>,
          <>CDC NHANES biomonitoring data shows dose-response relationships between urinary BPA/phthalates and abnormal TSH/T3/T4 levels.</>,
          <>Pregnant women and infants are especially vulnerable because thyroid hormones direct fetal brain development.</>,
          <>The same lifestyle changes that reduce general microplastic intake (filtered water, glass storage, no plastic-heated food) also reduce endocrine-chemical exposure.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why the thyroid is especially vulnerable
      </h2>
      <p>
        The thyroid gland produces hormones (T3, T4) that regulate metabolism,
        heart rate, body temperature, growth, and brain development. Production
        is finely tuned by the pituitary&apos;s TSH signal. Multiple plastic
        chemicals are <em>structurally similar</em> to thyroid hormones, allowing
        them to bind to thyroid receptors and interfere with normal signalling.
        Even very low doses can have measurable effects.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The four chemical families to know
      </h2>

      <BlogComparisonTable
        caption="Plastic-associated chemicals with documented thyroid effects"
        columns={[
          { key: 'chemical', label: 'Chemical family' },
          { key: 'source', label: 'Common sources' },
          { key: 'effect', label: 'Documented thyroid effect' },
        ]}
        rows={[
          { chemical: 'BPA (Bisphenol A)', source: 'Polycarbonate plastic, can liners, thermal receipts', effect: 'Antagonist at thyroid hormone receptor; alters TSH/T4 levels in NHANES data' },
          { chemical: 'BPS / BPF (BPA replacements)', source: '"BPA-free" plastics, food packaging', effect: 'Similar receptor activity to BPA; not safer per EFSA 2023 re-evaluation' },
          { chemical: 'Phthalates (DEHP, DBP, BBP)', source: 'PVC, vinyl, soft plastics, personal care', effect: 'Disrupts thyroid hormone synthesis; associated with hypothyroidism in pregnant women' },
          { chemical: 'PFAS (PFOA, PFOS, GenX)', source: 'Non-stick cookware, water-resistant clothing, food wrappers', effect: 'Reduces T4; alters TSH; classified by EPA as concerning thyroid disruptors' },
          { chemical: 'Brominated flame retardants', source: 'Plastic electronics, furniture foam', effect: 'Compete with thyroid hormones at carrier protein binding sites' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What human studies have found
      </h2>
      <p>
        The body of biomonitoring research (where researchers measure both
        chemical exposure and health markers in the same people) has produced
        several consistent findings:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>BPA + TSH:</strong> CDC NHANES studies have repeatedly shown a
          dose-response association between higher urinary BPA and altered TSH
          levels, particularly in adolescents and adults.
        </li>
        <li>
          <strong>Phthalates + pregnancy hypothyroidism:</strong> The TIDES study
          (Karwacka et al. 2019) found pregnant women in the highest quartile of
          phthalate exposure had measurably lower free T4 levels.
        </li>
        <li>
          <strong>PFAS + reduced T4:</strong> The C8 Science Panel studies of
          residents exposed to PFOA-contaminated drinking water found reduced
          T4 levels and elevated TSH in both adults and children.
        </li>
        <li>
          <strong>BPA + thyroid nodules:</strong> A 2020 Chinese cohort study
          found higher BPA exposure correlated with prevalence of thyroid
          nodules in women.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The pregnancy and infant angle
      </h2>
      <p>
        Maternal thyroid hormones are essential for fetal brain development,
        especially in the first trimester before the fetus produces its own.
        Maternal hypothyroidism (even mild) is associated with measurable
        reductions in child IQ and increased risk of neurodevelopmental
        disorders.
      </p>
      <p>
        For this reason, regulatory bodies (EFSA, the European Endocrine
        Society) have called for stronger restrictions on BPA, BPS, and certain
        phthalates in food-contact materials used during pregnancy. The EU&apos;s
        2025 microplastics restriction includes several relevant compounds.
      </p>
      <p>
        See also our{' '}
        <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in pregnancy
        </Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          BPA-free pregnancy guide
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical thyroid-protective changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Eliminate canned food and beverages</strong> when possible, can liners are a major BPA/BPS source.
        </li>
        <li>
          <strong>Avoid thermal receipts</strong> (gas station, retail), coated
          with BPA/BPS that absorbs through skin.
        </li>
        <li>
          <strong>Replace plastic food storage with glass.</strong> Eliminates
          phthalate and bisphenol migration during refrigeration and microwave.
        </li>
        <li>
          <strong>Switch from non-stick to cast-iron or stainless cookware</strong>, eliminates PFAS exposure at the highest-temperature contact point.
          See our{' '}
          <Link href="/blog/best-non-toxic-cookware" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            best non-toxic cookware guide
          </Link>.
        </li>
        <li>
          <strong>Filter your drinking water</strong>, addresses both microplastic
          particles and dissolved PFAS chemistry.
        </li>
        <li>
          <strong>Choose natural-fibre clothing</strong>, synthetic textiles
          contain brominated flame retardants and shed plasticisers.
        </li>
        <li>
          <strong>Read personal care product labels</strong> for phthalates
          (DEHP, DBP, BBP) often listed as &ldquo;fragrance&rdquo;.
        </li>
      </ol>


      <BlogWhatTheAppChecks
        items={[
          <>Product packaging. PET, HDPE, PP, PS, PVC, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, flags for known PFAS / BPA / fragranced lines.</>,
          <>Use-context flags, heat exposure, microwave, reuse cycles.</>,
          <>Cited research, every score links the specific studies behind it.</>,
        ]}
      />

      <BlogAppCTA
        headline="Translate the research into 5-second shelf decisions"
        sub="Reading the studies is step one. Acting on them at the grocery store is step two. The MicroPlastics app scores each product 0–100 using research like this."
        buttonLabel="Get the MicroPlastics app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do microplastics affect thyroid function?',
            answer:
              'Microplastics carry endocrine-disrupting chemicals (BPA, BPS, phthalates, PFAS) that interfere with thyroid hormone synthesis and receptor binding. Multiple human biomonitoring studies have linked plastic-chemical exposure to altered TSH, T3, and T4 levels.',
          },
          {
            question: 'Which plastic chemicals are worst for the thyroid?',
            answer:
              'BPA, BPS, BPF (bisphenols), phthalates (especially DEHP and DBP), PFAS chemicals (PFOA, PFOS), and brominated flame retardants. All have documented thyroid receptor binding or hormone-synthesis interference.',
          },
          {
            question: 'Are BPA-free plastics safer for the thyroid?',
            answer:
              'No. EFSA\'s 2023 re-evaluation and multiple peer-reviewed studies show that BPS and BPF (common BPA replacements) have similar endocrine-disrupting activity. "BPA-free" labelling does not mean endocrine-safe.',
          },
          {
            question: 'Is microplastic exposure during pregnancy dangerous for the thyroid?',
            answer:
              'Yes. Maternal thyroid hormones are essential for fetal brain development, especially in the first trimester. Higher phthalate exposure during pregnancy is associated with reduced free T4 levels (TIDES study). EFSA and the European Endocrine Society have called for stronger restrictions.',
          },
          {
            question: 'How can I protect my thyroid from plastic chemicals?',
            answer:
              'Eliminate canned food, switch to glass food storage, avoid thermal receipts, replace non-stick cookware with cast iron or stainless, filter drinking water, choose natural-fibre clothing, and read personal care labels for phthalates.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Andersson AM, Bay K, Frederiksen H, Skakkebæk NE',
            year: 2016,
            title: 'Endocrine disrupters: we need research, biomonitoring and action',
            publisher: 'Andrology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/27310506/',
          },
          {
            authors: 'Karwacka A, Zamkowska D, Radwan M, Jurewicz J',
            year: 2019,
            title: 'Exposure to modern, widespread environmental endocrine disrupting chemicals and their effect on the reproductive potential of women',
            publisher: 'Human Fertility',
            url: 'https://www.tandfonline.com/doi/full/10.1080/14647273.2017.1358828',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2023,
            title: 'Re-evaluation of bisphenol A (BPA)',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/6857',
          },
          {
            authors: 'Lewis RC, Meeker JD, Peterson KE, et al.',
            year: 2013,
            title: 'Predictors of urinary bisphenol A and phthalate metabolite concentrations in Mexican children',
            publisher: 'Chemosphere',
            url: 'https://pubmed.ncbi.nlm.nih.gov/23260261/',
          },
          {
            authors: 'C8 Science Panel',
            year: 2012,
            title: 'Probable Link Evaluation of Thyroid Disease',
            publisher: 'C8 Science Panel Reports',
            url: 'http://www.c8sciencepanel.org/prob_link.html',
          },
          {
            authors: 'US Environmental Protection Agency',
            year: 2024,
            title: 'PFOA, PFOS and Other PFAS: health effects',
            publisher: 'EPA',
            url: 'https://www.epa.gov/pfas/our-current-understanding-human-health-and-environmental-risks-pfas',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
